import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

/** Public origin the site is served from — matches src/server/seo/sitemap.ts's default. */
const SITE_ORIGIN = 'https://real-estate.tur92.com';

export interface SeoData {
  title: string;
  description: string;
  /** Route path including the leading slash, e.g. '/gradove'. */
  path: string;
  /** Comma-separated or array of target keywords for search engines. */
  keywords?: string[];
  /** OpenGraph type (defaults to 'website'). */
  ogType?: 'website' | 'article';
  /** Custom OpenGraph preview image URL (defaults to site-wide og-image.png). */
  ogImage?: string;
  /** Schema.org structured data (JSON-LD object or array of objects). */
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
}

/**
 * One place to set a page's title, description, canonical link, Open
 * Graph / Twitter preview tags, and Schema.org JSON-LD structured data.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);

  set({ title, description, path, keywords, ogType = 'website', ogImage, jsonLd }: SeoData): void {
    const url = `${SITE_ORIGIN}${path}`;
    const image = ogImage ?? `${SITE_ORIGIN}/og-image.png`;

    // 1. Basic meta
    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    if (keywords && keywords.length > 0) {
      this.meta.updateTag({ name: 'keywords', content: keywords.join(', ') });
    }

    // 2. OpenGraph
    this.meta.updateTag({ property: 'og:site_name', content: 'Имотомер' });
    this.meta.updateTag({ property: 'og:type', content: ogType });
    this.meta.updateTag({ property: 'og:locale', content: 'bg_BG' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:image:width', content: '1200' });
    this.meta.updateTag({ property: 'og:image:height', content: '630' });

    // 3. Twitter Cards
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });

    // 4. Canonical URL
    this.setCanonical(url);

    // 5. Schema.org JSON-LD Structured Data
    if (jsonLd) {
      this.setJsonLd(jsonLd);
    }
  }

  private setCanonical(url: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private setJsonLd(data: Record<string, unknown> | Array<Record<string, unknown>>): void {
    let script = this.document.querySelector<HTMLScriptElement>('script[type="application/ld+json"]#page-schema');
    if (!script) {
      script = this.document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('id', 'page-schema');
      this.document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
  }
}
